import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { WriteRequestOptions } from '../../requestOptions';
import type { ExpansionRun } from './types';

const validate = (runId: number, subscriptionId: number) => {
  if (!runId || runId < 1) {
    throw new Error('Valid run ID is required');
  }
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
};

type RequestParams = {
  runId: number;
  /**
   * Any subscription of the organization — the API authorises the caller against it and scopes
   * every read by organization, never by this id.
   */
  subscriptionId: number;
};

export type ResumeRunResponse = {
  /** The run, `running` again with the call that will carry it on. */
  run: ExpansionRun;
};

/**
 * Carries on an interrupted (or stalled) run from what it stored. 409 when the run is still live,
 * did not stop (complete, failed), or another preview of its destination has started since.
 */
export const resumeRun = async (
  scope: RequestScope,
  { runId, subscriptionId }: RequestParams,
  options: WriteRequestOptions
): Promise<ChannelApiResponse<ResumeRunResponse>> => {
  try {
    validate(runId, subscriptionId);

    const result = await scopedChannelApi(scope).post<ResumeRunResponse>(
      `atts/expansion/runs/${runId}/resume`,
      {},
      {
        ...options,
        headers: {
          'x-astur-subscription-id': subscriptionId.toString(),
        },
      }
    );

    return {
      data: result.data,
      status: result.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
